import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtDateTime, inr } from "../../lib/api";
import SEO from "../../components/SEO";
import { statusLabel } from "../../lib/labels";

const TABS = [
  ["overview", "Overview"],
  ["rates", "Gold Rates"],
  ["leads", "Leads"],
  ["projects", "Projects"],
  ["products", "Products"],
  ["content", "Content"],
  ["team", "Team & Offices"],
  ["news", "News"],
  ["users", "Users"],
  ["activity", "Activity Log"],
];

const ROLES = ["super_admin", "jewellery_admin", "real_estate_admin", "content_editor", "sales", "read_only", "pending"];
const LEAD_STATUSES = ["new", "contacted", "in_progress", "closed", "spam"];

const input = "w-full bg-[#060A12] border border-white/15 px-3 py-2.5 text-sm text-[#FBF9F5] outline-none focus:border-[#C5A059]/70";
const label = "block text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] mb-1.5 font-semibold";
const btn = "text-[10px] uppercase tracking-[0.18em] font-semibold bg-[#C5A059] text-[#060A12] px-5 py-2.5 hover:bg-[#d6b678] transition-colors duration-300 disabled:opacity-50";
const btnGhost = "text-[10px] uppercase tracking-[0.18em] font-semibold border border-white/20 text-[#FBF9F5] px-4 py-2.5 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-300";

export default function AdminDashboard({ user, onLogout }) {
  const [tab, setTab] = useState("overview");
  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#060A12] text-[#FBF9F5]">
      <SEO title="Administration" />
      <header className="border-b border-[#C5A059]/15 bg-[#0B1320]">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <p className="font-serif text-xl">
            RK <span className="text-[#C5A059] italic">Group</span>
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#94A3B8] ml-3 font-sans font-semibold">Admin</span>
          </p>
          <div className="flex items-center gap-4">
            <span className="text-xs text-[#94A3B8] hidden sm:block" data-testid="admin-user-label">
              {user.name} · <span className="text-[#C5A059]">{user.role}</span>
            </span>
            <button data-testid="admin-logout-btn" onClick={onLogout} className={btnGhost}>Sign out</button>
          </div>
        </div>
        <nav className="max-w-[1500px] mx-auto px-5 sm:px-8 flex gap-1 overflow-x-auto" data-testid="admin-tabs">
          {TABS.map(([k, l]) => (
            <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
              className={`px-4 py-3 text-[11px] uppercase tracking-[0.15em] font-semibold whitespace-nowrap border-b-2 transition-colors duration-300 ${
                tab === k ? "border-[#C5A059] text-[#C5A059]" : "border-transparent text-[#94A3B8] hover:text-[#FBF9F5]"
              }`}>
              {l}
            </button>
          ))}
        </nav>
      </header>
      <main className="max-w-[1500px] mx-auto px-5 sm:px-8 py-10">
        {tab === "overview" && <Overview />}
        {tab === "rates" && <RatesAdmin user={user} />}
        {tab === "leads" && <LeadsAdmin />}
        {tab === "projects" && <ProjectsAdmin />}
        {tab === "products" && <ProductsAdmin />}
        {tab === "content" && <ContentAdmin />}
        {tab === "team" && <TeamAdmin />}
        {tab === "news" && <NewsAdmin />}
        {tab === "users" && <UsersAdmin user={user} />}
        {tab === "activity" && <ActivityAdmin />}
      </main>
    </div>
  );
}

function Overview() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return <p className="text-sm text-[#94A3B8]">Loading…</p>;
  const divs = ["jewellery", "real_estate", "group"];
  return (
    <div data-testid="admin-overview">
      <h2 className="font-serif text-3xl mb-8">Overview</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
        {divs.map((d) => {
          const total = Object.values(data.leads?.[d] || {}).reduce((a, b) => a + b, 0);
          const fresh = data.leads?.[d]?.new || 0;
          return (
            <div key={d} className="bg-[#060A12] p-8">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] capitalize">{d.replace("_", " ")} leads</p>
              <p className="font-serif text-4xl mt-3">{total}</p>
              <p className="text-xs text-[#C5A059] mt-2">{fresh} new</p>
            </div>
          );
        })}
        <div className="bg-[#060A12] p-8">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">Catalogue</p>
          <p className="font-serif text-4xl mt-3">{data.projects} <span className="text-lg text-[#94A3B8]">projects</span></p>
          <p className="font-serif text-4xl mt-1">{data.products} <span className="text-lg text-[#94A3B8]">products</span></p>
        </div>
      </div>
    </div>
  );
}

function RatesAdmin({ user }) {
  const [rates, setRates] = useState(null);
  const [form, setForm] = useState({ gold_24k_10g: "", gold_22k_10g: "", gold_18k_10g: "", silver_kg: "", mode: "manual", paused: false, adjustment_pct: 0, source: "", note: "" });
  const [audit, setAudit] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const canEdit = ["super_admin", "jewellery_admin"].includes(user.role);

  const refreshNow = async () => {
    setRefreshing(true);
    try {
      await api.post("/admin/rates/refresh");
      toast.success("Latest rates fetched from Goodreturns.");
      load();
    } catch {
      toast.error("Source unreachable — last verified rates remain displayed.");
    } finally {
      setRefreshing(false);
    }
  };

  const load = () => {
    api.get("/rates").then((r) => setRates(r.data)).catch(() => {});
    api.get("/admin/rate-audit").then((r) => setAudit(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      mode: form.mode,
      paused: form.paused,
      adjustment_pct: Number(form.adjustment_pct) || 0,
      source: form.source || null,
      note: form.note || null,
    };
    if (form.mode !== "api") {
      payload.gold_24k_10g = form.gold_24k_10g === "" ? null : Number(form.gold_24k_10g);
      payload.gold_22k_10g = form.gold_22k_10g === "" ? null : Number(form.gold_22k_10g);
      payload.gold_18k_10g = form.gold_18k_10g === "" ? null : Number(form.gold_18k_10g);
      payload.silver_kg = form.silver_kg === "" ? null : Number(form.silver_kg);
    }
    try {
      await api.put("/admin/rates", payload);
      toast.success("Rates updated and recorded in the audit log.");
      load();
    } catch {
      toast.error("Could not update rates.");
    }
  };

  return (
    <div data-testid="admin-rates" className="grid grid-cols-1 lg:grid-cols-2 gap-10">
      <div>
        <h2 className="font-serif text-3xl mb-2">Gold &amp; Silver Rates</h2>
        <p className="text-xs text-[#94A3B8] mb-8 leading-relaxed">
          Enter base rates per 10 g (gold) and per kg (silver). An authorised adjustment (%) can be applied on top.
          Every change records the person, date, and time.
        </p>
        {!canEdit && <p className="text-sm text-[#C5A059] mb-6">Your role is read-only for rates.</p>}
        {rates && (
          <p className="text-xs text-[#94A3B8] mb-6" data-testid="rates-current">
            Current: 24K {inr(rates.gold_24k.per_10g)}/10g · 22K {inr(rates.gold_22k.per_10g)}/10g · 18K {inr(rates.gold_18k.per_10g)}/10g · Silver {inr(rates.silver.per_gram)}/g
            {rates.updated_at && <> · updated {fmtDateTime(rates.updated_at)} by {rates.updated_by}</>}
          </p>
        )}
        <form onSubmit={save} className="space-y-5" data-testid="rates-form">
          <div className="grid grid-cols-2 gap-5">
            <div><span className={label}>24K gold — ₹ / 10 g</span><input data-testid="rates-gold-24k" className={input} type="number" step="0.01" value={form.gold_24k_10g} onChange={(e) => set("gold_24k_10g", e.target.value)} disabled={!canEdit} /></div>
            <div><span className={label}>22K gold — ₹ / 10 g</span><input data-testid="rates-gold-22k" className={input} type="number" step="0.01" value={form.gold_22k_10g} onChange={(e) => set("gold_22k_10g", e.target.value)} disabled={!canEdit} /></div>
            <div><span className={label}>18K gold — ₹ / 10 g</span><input data-testid="rates-gold-18k" className={input} type="number" step="0.01" value={form.gold_18k_10g} onChange={(e) => set("gold_18k_10g", e.target.value)} disabled={!canEdit} /></div>
            <div><span className={label}>Silver — ₹ / kg</span><input data-testid="rates-silver" className={input} type="number" step="0.01" value={form.silver_kg} onChange={(e) => set("silver_kg", e.target.value)} disabled={!canEdit} /></div>
            <div>
              <span className={label}>Rate mode</span>
              <select data-testid="rates-mode" className={input} value={form.mode} onChange={(e) => set("mode", e.target.value)} disabled={!canEdit}>
                <option value="manual">Manual entry</option>
                <option value="api">API rate (when connected)</option>
              </select>
            </div>
            <div><span className={label}>Authorised adjustment %</span><input data-testid="rates-adjustment" className={input} type="number" step="0.01" value={form.adjustment_pct} onChange={(e) => set("adjustment_pct", e.target.value)} disabled={!canEdit} /></div>
            <div><span className={label}>Rate source label</span><input data-testid="rates-source" className={input} value={form.source} onChange={(e) => set("source", e.target.value)} placeholder="e.g. Manual — admin desk" disabled={!canEdit} /></div>
            <div><span className={label}>Public note</span><input data-testid="rates-note" className={input} value={form.note} onChange={(e) => set("note", e.target.value)} disabled={!canEdit} /></div>
          </div>
          <label className="flex items-center gap-3 text-xs text-[#94A3B8]">
            <input type="checkbox" data-testid="rates-paused" checked={form.paused} onChange={(e) => set("paused", e.target.checked)} className="accent-[#C5A059]" disabled={!canEdit} />
            Pause automatic updates (display last verified rate)
          </label>
          {canEdit && <button type="submit" data-testid="rates-save-btn" className={btn}>Publish Rates</button>}
          <button type="button" data-testid="rates-fetch-btn" onClick={refreshNow} disabled={refreshing}
            className={`${btnGhost} ml-3`}>
            {refreshing ? "Fetching…" : "Fetch Latest from Goodreturns"}
          </button>
          {form.mode === "api" && (
            <p className="text-xs text-[#94A3B8] leading-relaxed">
              API mode: rates refresh automatically from Goodreturns.in (Meerut) every 6 hours on the first
              visitor request, with every change recorded below. If the source is unreachable, the last
              verified rate stays displayed with its timestamp.
            </p>
          )}
        </form>
      </div>
      <div>
        <h3 className="font-serif text-2xl mb-6">Rate Change Audit</h3>
        <div className="space-y-px max-h-[560px] overflow-y-auto border border-white/10" data-testid="rate-audit-list">
          {audit.length === 0 && <p className="text-sm text-[#94A3B8] p-6">No rate changes recorded yet.</p>}
          {audit.map((a) => (
            <div key={a.id} className="bg-[#0B1320] p-5 text-xs">
              <p className="text-[#FBF9F5]">{a.changed_by} <span className="text-[#94A3B8]">· {fmtDateTime(a.changed_at)}</span></p>
              <p className="text-[#94A3B8] mt-2">
                24K {a.gold_24k_10g ?? "—"} · 22K {a.gold_22k_10g ?? "—"} · 18K {a.gold_18k_10g ?? "—"} · Ag {a.silver_kg ?? "—"}/kg · adj {a.adjustment_pct}% · {a.mode}{a.paused ? " · paused" : ""}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function LeadsAdmin() {
  const [division, setDivision] = useState("jewellery");
  const [leads, setLeads] = useState([]);
  const load = () => api.get(`/admin/enquiries?division=${division}`).then((r) => setLeads(r.data)).catch(() => {});
  useEffect(() => { load(); }, [division]);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/enquiries/${id}`, { status }).then(load).catch(() => toast.error("Update failed"));
  };

  return (
    <div data-testid="admin-leads">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h2 className="font-serif text-3xl">Leads</h2>
        <div className="flex gap-2">
          {[["jewellery", "Jewellery"], ["real_estate", "Real Estate"], ["group", "Group & Other"]].map(([v, l]) => (
            <button key={v} data-testid={`leads-filter-${v}`} onClick={() => setDivision(v)}
              className={`${btnGhost} ${division === v ? "!border-[#C5A059] !text-[#C5A059]" : ""}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="space-y-px border border-white/10" data-testid="leads-list">
        {leads.length === 0 && <p className="text-sm text-[#94A3B8] p-6">No {division.replace("_", " ")} leads yet.</p>}
        {leads.map((l) => (
          <div key={l.id} className="bg-[#0B1320] p-5 grid grid-cols-1 lg:grid-cols-12 gap-4 items-start" data-testid={`lead-${l.id}`}>
            <div className="lg:col-span-3">
              <p className="text-sm font-semibold">{l.name}</p>
              <p className="text-xs text-[#94A3B8] mt-1">{l.phone}{l.email ? ` · ${l.email}` : ""}</p>
              <p className="text-[10px] uppercase tracking-[0.15em] text-[#C5A059] mt-2">{l.type.replace(/_/g, " ")}</p>
            </div>
            <div className="lg:col-span-5 text-xs text-[#94A3B8] leading-relaxed">
              {l.message && <p className="mb-1">{l.message}</p>}
              {Object.entries(l.payload || {}).map(([k, v]) => (
                <p key={k}><span className="text-[#FBF9F5]/60 capitalize">{k.replace(/_/g, " ")}:</span> {String(v)}</p>
              ))}
              <p className="mt-2 text-[#94A3B8]/60">{fmtDateTime(l.created_at)}{l.page ? ` · from ${l.page}` : ""}</p>
            </div>
            <div className="lg:col-span-4 flex items-center gap-3">
              <select data-testid={`lead-status-${l.id}`} className={input} value={l.status} onChange={(e) => setStatus(l.id, e.target.value)}>
                {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
              </select>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProjectsAdmin() {
  const [projects, setProjects] = useState([]);
  const load = () => api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const update = async (p, patch) => {
    try {
      await api.put(`/admin/projects/${p.id}`, { ...p, ...patch });
      toast.success("Project updated");
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <div data-testid="admin-projects">
      <h2 className="font-serif text-3xl mb-8">Real Estate Projects</h2>
      <div className="space-y-px border border-white/10">
        {projects.length === 0 && (
          <p className="text-sm text-[#94A3B8] p-6">
            No developments published. Add a development here only once its name, specifications and approvals are verified.
          </p>
        )}
        {projects.map((p) => (
          <div key={p.id} className="bg-[#0B1320] p-5 grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
            <div className="lg:col-span-3">
              <p className="text-sm font-semibold">{p.location}</p>
              <p className="text-xs text-[#94A3B8] mt-0.5 truncate">{p.name}</p>
            </div>
            <div className="lg:col-span-2">
              <span className={label}>Status</span>
              <select data-testid={`project-status-${p.slug}`} className={input} value={p.status} onChange={(e) => update(p, { status: e.target.value })}>
                {["completed", "ready_for_possession", "under_development", "booking_open", "coming_soon", "sold_out"].map((s) => (
                  <option key={s} value={s}>{statusLabel(s)}</option>
                ))}
              </select>
            </div>
            <div className="lg:col-span-3">
              <span className={label}>Official name</span>
              <input className={input} defaultValue={p.name} onBlur={(e) => e.target.value !== p.name && update(p, { name: e.target.value })} />
            </div>
            <div className="lg:col-span-2">
              <span className={label}>RERA</span>
              <input className={input} defaultValue={p.rera} onBlur={(e) => e.target.value !== p.rera && update(p, { rera: e.target.value })} />
            </div>
            <div className="lg:col-span-2">
              <span className={label}>Starting price</span>
              <input className={input} defaultValue={p.price} onBlur={(e) => e.target.value !== p.price && update(p, { price: e.target.value })} />
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-[#94A3B8] mt-4">Changes save when you leave a field. Full project editing (galleries, plans, brochures) is next on the build list.</p>
    </div>
  );
}

function ProductsAdmin() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ name: "", code: "", category: "gold-jewellery", metal: "", purity: "", weight_range: "", available: true });
  const load = () => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/jewellery/categories").then((r) => setCategories(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/products", { ...form });
      toast.success("Product added");
      setForm({ name: "", code: "", category: "gold-jewellery", metal: "", purity: "", weight_range: "", available: true });
      load();
    } catch {
      toast.error("Could not add product");
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/products/${id}`).then(load).catch(() => toast.error("Delete failed"));
  };

  return (
    <div data-testid="admin-products" className="grid grid-cols-1 lg:grid-cols-3 gap-10">
      <div>
        <h2 className="font-serif text-3xl mb-8">Add Product</h2>
        <form onSubmit={add} className="space-y-5" data-testid="product-add-form">
          <div><span className={label}>Product name *</span><input required data-testid="product-name" className={input} value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
          <div><span className={label}>Product code</span><input data-testid="product-code" className={input} value={form.code} onChange={(e) => set("code", e.target.value)} placeholder="e.g. RKS-CH-1042" /></div>
          <div>
            <span className={label}>Category</span>
            <select data-testid="product-category" className={input} value={form.category} onChange={(e) => set("category", e.target.value)}>
              {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><span className={label}>Metal</span><input className={input} value={form.metal} onChange={(e) => set("metal", e.target.value)} placeholder="Gold" /></div>
            <div><span className={label}>Purity</span><input className={input} value={form.purity} onChange={(e) => set("purity", e.target.value)} placeholder="22K (916)" /></div>
          </div>
          <div><span className={label}>Approx. weight range</span><input className={input} value={form.weight_range} onChange={(e) => set("weight_range", e.target.value)} placeholder="e.g. 8–24 g" /></div>
          <button type="submit" data-testid="product-add-btn" className={btn}>Add to Catalogue</button>
        </form>
      </div>
      <div className="lg:col-span-2">
        <h3 className="font-serif text-2xl mb-6">Catalogue ({products.length})</h3>
        <div className="space-y-px border border-white/10 max-h-[600px] overflow-y-auto">
          {products.length === 0 && <p className="text-sm text-[#94A3B8] p-6">No products yet.</p>}
          {products.map((p) => (
            <div key={p.id} className="bg-[#0B1320] p-4 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm">{p.name} {p.code && <span className="text-[#C5A059] text-xs ml-2">{p.code}</span>}</p>
                <p className="text-[11px] text-[#94A3B8] mt-1 capitalize">{p.category.replace(/-/g, " ")} · {p.metal || "—"} · {p.purity || "—"} · {p.weight_range || "—"}</p>
              </div>
              <button data-testid={`product-delete-${p.id}`} onClick={() => remove(p.id)} className={btnGhost}>Delete</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ContentAdmin() {
  const [content, setContent] = useState({});
  const [drafts, setDrafts] = useState({});
  useEffect(() => {
    api.get("/content").then((r) => {
      setContent(r.data);
      setDrafts(Object.fromEntries(Object.entries(r.data).map(([k, v]) => [k, JSON.stringify(v, null, 2)])));
    }).catch(() => {});
  }, []);

  const save = async (key) => {
    try {
      const data = JSON.parse(drafts[key]);
      await api.put(`/admin/content/${key}`, { data });
      toast.success(`${key} saved`);
    } catch {
      toast.error("Invalid JSON — not saved");
    }
  };

  return (
    <div data-testid="admin-content">
      <h2 className="font-serif text-3xl mb-4">Site Content</h2>
      <p className="text-xs text-[#94A3B8] mb-8 max-w-2xl leading-relaxed">
        Editable CMS blocks. Glance numbers accept a number or <code className="text-[#C5A059]">null</code> (shown as
        “awaiting verification”). Timeline dates accept a string or null. Publish only verified values.
      </p>
      {Object.keys(content).map((key) => (
        <div key={key} className="mb-8">
          <span className={label}>{key}</span>
          <textarea data-testid={`content-${key}`} rows={key === "glance" ? 14 : 12} className={`${input} font-mono text-xs`}
            value={drafts[key] || ""} onChange={(e) => setDrafts((p) => ({ ...p, [key]: e.target.value }))} />
          <button data-testid={`content-save-${key}`} onClick={() => save(key)} className={`${btn} mt-3`}>Save {key}</button>
        </div>
      ))}
    </div>
  );
}

function TeamAdmin() {
  const [directors, setDirectors] = useState([]);
  const [offices, setOffices] = useState([]);
  const [config, setConfig] = useState({});
  const load = () => {
    api.get("/directors").then((r) => setDirectors(r.data)).catch(() => {});
    api.get("/offices").then((r) => setOffices(r.data)).catch(() => {});
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveDirector = async (d) => {
    await api.put(`/admin/directors/${d.id}`, d).then(() => toast.success(`${d.name} updated`)).catch(() => toast.error("Failed"));
  };
  const saveOffice = async (o) => {
    await api.put(`/admin/offices/${o.id}`, o).then(() => toast.success(`${o.name} updated`)).catch(() => toast.error("Failed"));
  };
  const saveConfig = async () => {
    await api.put("/admin/config", config).then(() => toast.success("Settings saved")).catch(() => toast.error("Failed"));
  };

  return (
    <div data-testid="admin-team" className="space-y-14">
      <section>
        <h2 className="font-serif text-3xl mb-8">Directors</h2>
        <div className="space-y-px border border-white/10">
          {directors.map((d) => (
            <div key={d.id} className="bg-[#0B1320] p-5 grid grid-cols-1 lg:grid-cols-12 gap-4">
              <p className="lg:col-span-2 text-sm font-semibold pt-2">{d.name}</p>
              <input className={`${input} lg:col-span-3`} defaultValue={d.role} data-testid={`director-role-${d.order}`}
                onBlur={(e) => e.target.value !== d.role && saveDirector({ ...d, role: e.target.value })} />
              <textarea className={`${input} lg:col-span-6`} rows={2} defaultValue={d.bio} data-testid={`director-bio-${d.order}`}
                onBlur={(e) => e.target.value !== d.bio && saveDirector({ ...d, bio: e.target.value })} />
            </div>
          ))}
        </div>
      </section>
      <section>
        <h2 className="font-serif text-3xl mb-8">Offices &amp; Showroom</h2>
        <div className="space-y-px border border-white/10">
          {offices.map((o) => (
            <div key={o.id} className="bg-[#0B1320] p-5 grid grid-cols-1 lg:grid-cols-12 gap-4">
              <p className="lg:col-span-3 text-sm font-semibold pt-2">{o.name}<span className="block text-[10px] text-[#94A3B8] font-normal mt-1">{o.department}</span></p>
              <input className={`${input} lg:col-span-4`} defaultValue={o.address} placeholder="Full address"
                onBlur={(e) => e.target.value !== o.address && saveOffice({ ...o, address: e.target.value })} />
              <input className={`${input} lg:col-span-2`} defaultValue={o.phone} placeholder="Phone"
                onBlur={(e) => e.target.value !== o.phone && saveOffice({ ...o, phone: e.target.value })} />
              <input className={`${input} lg:col-span-3`} defaultValue={o.maps_url} placeholder="Google Maps link"
                onBlur={(e) => e.target.value !== o.maps_url && saveOffice({ ...o, maps_url: e.target.value })} />
            </div>
          ))}
        </div>
      </section>
      <section>
        <h2 className="font-serif text-3xl mb-8">WhatsApp &amp; Contact Settings</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl">
          {[["whatsapp_jewellery", "WhatsApp — jewellery (e.g. 91XXXXXXXXXX)"], ["whatsapp_realestate", "WhatsApp — real estate"], ["email_jewellery", "Email — jewellery"], ["email_realestate", "Email — real estate"]].map(([k, l]) => (
            <div key={k}>
              <span className={label}>{l}</span>
              <input className={input} data-testid={`config-${k}`} value={config[k] || ""}
                onChange={(e) => setConfig((p) => ({ ...p, [k]: e.target.value }))} />
            </div>
          ))}
        </div>
        <button data-testid="config-save-btn" onClick={saveConfig} className={`${btn} mt-6`}>Save Settings</button>
      </section>
    </div>
  );
}

function NewsAdmin() {
  const [news, setNews] = useState([]);
  const [form, setForm] = useState({ title: "", date: new Date().toISOString().slice(0, 10), tag: "Announcement", body: "" });
  const load = () => api.get("/news").then((r) => setNews(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    await api.post("/admin/news", form).then(() => { toast.success("Published"); setForm({ ...form, title: "", body: "" }); load(); })
      .catch(() => toast.error("Failed"));
  };

  return (
    <div data-testid="admin-news" className="grid grid-cols-1 lg:grid-cols-2 gap-10">
      <form onSubmit={add} className="space-y-5">
        <h2 className="font-serif text-3xl mb-2">Publish Update</h2>
        <div><span className={label}>Title *</span><input required data-testid="news-title" className={input} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><span className={label}>Date</span><input type="date" className={input} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
          <div>
            <span className={label}>Tag</span>
            <select className={input} value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })}>
              {["Announcement", "Jewellery", "Real Estate", "Media"].map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div><span className={label}>Body</span><textarea rows={5} className={input} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></div>
        <button type="submit" data-testid="news-publish-btn" className={btn}>Publish</button>
      </form>
      <div className="space-y-px border border-white/10 max-h-[480px] overflow-y-auto">
        {news.map((n) => (
          <div key={n.id} className="bg-[#0B1320] p-5 flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-semibold">{n.title}</p>
              <p className="text-[11px] text-[#94A3B8] mt-1">{n.tag} · {n.date}</p>
            </div>
            <button onClick={() => api.delete(`/admin/news/${n.id}`).then(load)} className={btnGhost}>Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function UsersAdmin({ user }) {
  const [users, setUsers] = useState([]);
  const isSuper = user.role === "super_admin";
  const load = () => isSuper && api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!isSuper) return <p className="text-sm text-[#94A3B8]">Only the Super Administrator can manage users and roles.</p>;

  const setRole = async (userId, role) => {
    await api.patch(`/admin/users/${userId}`, { role }).then(() => { toast.success("Role updated"); load(); })
      .catch(() => toast.error("Failed"));
  };

  return (
    <div data-testid="admin-users">
      <h2 className="font-serif text-3xl mb-8">Users &amp; Roles</h2>
      <div className="space-y-px border border-white/10">
        {users.map((u) => (
          <div key={u.user_id} className="bg-[#0B1320] p-5 grid grid-cols-1 sm:grid-cols-3 gap-4 items-center">
            <div>
              <p className="text-sm font-semibold">{u.name}</p>
              <p className="text-xs text-[#94A3B8]">{u.email}</p>
            </div>
            <p className="text-[11px] text-[#94A3B8]">Joined {fmtDateTime(u.created_at)}</p>
            <select data-testid={`user-role-${u.user_id}`} className={input} value={u.role} onChange={(e) => setRole(u.user_id, e.target.value)}>
              {ROLES.map((r) => <option key={r} value={r}>{r.replace("_", " ")}</option>)}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}

function ActivityAdmin() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/admin/activity").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  return (
    <div data-testid="admin-activity">
      <h2 className="font-serif text-3xl mb-8">Activity Log</h2>
      <div className="space-y-px border border-white/10 max-h-[640px] overflow-y-auto">
        {items.length === 0 && <p className="text-sm text-[#94A3B8] p-6">No activity recorded yet.</p>}
        {items.map((a) => (
          <div key={a.id} className="bg-[#0B1320] p-4 text-xs flex flex-wrap gap-x-4 gap-y-1">
            <span className="text-[#C5A059] uppercase tracking-[0.15em]">{a.action.replace(/_/g, " ")}</span>
            <span className="text-[#FBF9F5]/80">{a.user_email}</span>
            <span className="text-[#94A3B8]">{a.details}</span>
            <span className="text-[#94A3B8]/60 ml-auto">{fmtDateTime(a.created_at)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
